/**
 * Página /#events — overview + lista filtrável de DeviceEvent.
 *
 * Cards: byCategory + bySeverity nas últimas 24h + top devices barulhentos.
 * Tabela paginada com filtros: severity, category, search.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';

const SEVERITY_COLOR: Record<string, string> = {
  emergency: 'text-rose-500',
  alert:     'text-rose-500',
  critical:  'text-rose-400',
  error:     'text-amber-400',
  warning:   'text-yellow-400',
  notice:    'text-cyan-400',
  info:      'text-slate-300',
  debug:     'text-slate-500'
};
const CATEGORY_COLOR: Record<string, string> = {
  bgp:       'text-amber-400',
  interface: 'text-cyan-400',
  optical:   'text-rose-400',
  lldp:      'text-violet-400',
  mpls:      'text-fuchsia-400',
  auth:      'text-emerald-400',
  system:    'text-slate-400',
  misc:      'text-slate-500'
};

type Overview = {
  since: string; total: number;
  bySeverity: Record<string, number>;
  byCategory: Record<string, number>;
  topNoisyDevices: Array<{
    deviceId: number;
    count: number;
    device: { id: number; hostname: string; mgmtIp: string } | null;
  }>;
};

type EventRow = {
  id: number;
  ts: string;
  deviceId: number;
  severity: string;
  source: string;
  category: string | null;
  message: string;
  parsed: Record<string, unknown> | null;
  device: { id: number; hostname: string; mgmtIp: string; vendor: { code: string } };
};

export function EventsPage() {
  const ovQ = useQuery<Overview>({
    queryKey: ['events', 'overview'],
    queryFn: () => api.get('/api/events/overview'),
    refetchInterval: 30_000
  });
  const [severity, setSeverity] = useState('');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [hours, setHours] = useState(24);
  const [skip, setSkip] = useState(0);
  const take = 50;

  const listQ = useQuery<{ total: number; take: number; skip: number; events: EventRow[] }>({
    queryKey: ['events', 'list', severity, category, search, hours, skip],
    queryFn: () => {
      const qs = new URLSearchParams({ take: String(take), skip: String(skip), hours: String(hours) });
      if (severity) qs.set('severity', severity);
      if (category) qs.set('category', category);
      if (search)   qs.set('search', search);
      return api.get(`/api/events?${qs}`);
    },
    refetchInterval: 15_000
  });

  return (
    <div className="p-6 space-y-4">
      <h2 className="text-xl font-semibold">Eventos <span className="text-sm text-slate-500">— últimas 24h</span></h2>

      {ovQ.data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <SeverityCard ov={ovQ.data} onClick={setSeverity} active={severity} />
          <CategoryCard ov={ovQ.data} onClick={setCategory} active={category} />
          <NoisyDevicesCard ov={ovQ.data} />
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <Picker label="Severidade" value={severity} onChange={(v) => { setSeverity(v); setSkip(0); }}
                options={['', 'emergency', 'alert', 'critical', 'error', 'warning', 'notice', 'info', 'debug']} />
        <Picker label="Categoria" value={category} onChange={(v) => { setCategory(v); setSkip(0); }}
                options={['', 'bgp', 'interface', 'lldp', 'optical', 'mpls', 'system', 'auth', 'misc']} />
        <label className="flex items-center gap-2 text-sm">
          <span className="text-slate-400">Janela</span>
          <select className="bg-slate-800 border border-slate-700 rounded px-2 py-1"
                  value={hours} onChange={(e) => { setHours(Number(e.target.value)); setSkip(0); }}>
            <option value={1}>1h</option><option value={6}>6h</option>
            <option value={24}>24h</option><option value={72}>3d</option><option value={168}>7d</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm flex-1 min-w-[200px]">
          <span className="text-slate-400">Buscar</span>
          <input className="flex-1 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm"
                 value={search} onChange={(e) => { setSearch(e.target.value); setSkip(0); }}
                 placeholder="texto na message…" />
        </label>
      </div>

      <div className="rounded border border-slate-800 bg-slate-900/60 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-800/60 text-slate-400">
            <tr>
              <th className="text-left px-3 py-2">Quando</th>
              <th className="text-left px-3 py-2">Sev</th>
              <th className="text-left px-3 py-2">Categoria</th>
              <th className="text-left px-3 py-2">Device</th>
              <th className="text-left px-3 py-2">Source</th>
              <th className="text-left px-3 py-2">Mensagem</th>
            </tr>
          </thead>
          <tbody>
            {listQ.isLoading && <tr><td colSpan={6} className="p-4 text-center text-slate-500">carregando…</td></tr>}
            {listQ.data?.events.map((e) => (
              <tr key={e.id} className="border-t border-slate-800">
                <td className="px-3 py-1.5 font-mono text-xs text-slate-400 whitespace-nowrap">
                  {new Date(e.ts).toLocaleString('pt-BR', { hour12: false })}
                </td>
                <td className={`px-3 py-1.5 font-mono text-xs ${SEVERITY_COLOR[e.severity] ?? ''}`}>{e.severity}</td>
                <td className={`px-3 py-1.5 ${CATEGORY_COLOR[e.category ?? 'misc'] ?? ''}`}>{e.category ?? '—'}</td>
                <td className="px-3 py-1.5 text-slate-300 text-xs whitespace-nowrap" title={e.device.hostname}>
                  {e.device.hostname.length > 40 ? e.device.hostname.slice(0, 40) + '…' : e.device.hostname}
                </td>
                <td className="px-3 py-1.5 text-slate-500 text-xs">{e.source}</td>
                <td className="px-3 py-1.5 text-slate-300" title={JSON.stringify(e.parsed)}>{e.message}</td>
              </tr>
            ))}
            {listQ.data?.events.length === 0 && (
              <tr><td colSpan={6} className="p-4 text-center text-slate-500">nenhum evento encontrado</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {listQ.data && (
        <div className="flex items-center justify-between text-sm">
          <div className="text-slate-500">
            mostrando {Math.min(skip + 1, listQ.data.total)}–{Math.min(skip + listQ.data.events.length, listQ.data.total)} de {listQ.data.total.toLocaleString('pt-BR')}
          </div>
          <div className="flex gap-2">
            <button onClick={() => setSkip(Math.max(0, skip - take))} disabled={skip === 0}
                    className="px-3 py-1 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-30">← anterior</button>
            <button onClick={() => setSkip(skip + take)} disabled={skip + take >= listQ.data.total}
                    className="px-3 py-1 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-30">próxima →</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Picker({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: string[] }) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="text-slate-400">{label}</span>
      <select className="bg-slate-800 border border-slate-700 rounded px-2 py-1"
              value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => <option key={o} value={o}>{o === '' ? '— todos —' : o}</option>)}
      </select>
    </label>
  );
}

function SeverityCard({ ov, onClick, active }: { ov: Overview; onClick: (s: string) => void; active: string }) {
  return (
    <div className="rounded border border-slate-800 bg-slate-900/60 p-3">
      <div className="text-xs text-slate-500 uppercase mb-2">Severidade · {ov.total.toLocaleString('pt-BR')} total</div>
      <div className="space-y-1">
        {Object.entries(ov.bySeverity).sort((a, b) => b[1] - a[1]).map(([sev, n]) => (
          <button key={sev} onClick={() => onClick(active === sev ? '' : sev)}
                  className={`w-full flex items-center justify-between gap-2 px-2 py-1 rounded text-sm hover:bg-slate-800 ${active === sev ? 'bg-slate-800 ring-1 ring-cyan-700' : ''}`}>
            <span className={SEVERITY_COLOR[sev] ?? ''}>{sev}</span>
            <span className="text-slate-300 font-mono text-xs">{n.toLocaleString('pt-BR')}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function CategoryCard({ ov, onClick, active }: { ov: Overview; onClick: (c: string) => void; active: string }) {
  return (
    <div className="rounded border border-slate-800 bg-slate-900/60 p-3">
      <div className="text-xs text-slate-500 uppercase mb-2">Categoria</div>
      <div className="space-y-1">
        {Object.entries(ov.byCategory).sort((a, b) => b[1] - a[1]).map(([cat, n]) => (
          <button key={cat} onClick={() => onClick(active === cat ? '' : cat)}
                  className={`w-full flex items-center justify-between gap-2 px-2 py-1 rounded text-sm hover:bg-slate-800 ${active === cat ? 'bg-slate-800 ring-1 ring-cyan-700' : ''}`}>
            <span className={CATEGORY_COLOR[cat] ?? ''}>{cat}</span>
            <span className="text-slate-300 font-mono text-xs">{n.toLocaleString('pt-BR')}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function NoisyDevicesCard({ ov }: { ov: Overview }) {
  return (
    <div className="rounded border border-slate-800 bg-slate-900/60 p-3">
      <div className="text-xs text-slate-500 uppercase mb-2">Top devices (warning+)</div>
      <div className="space-y-1 text-sm">
        {ov.topNoisyDevices.slice(0, 8).map((r) => (
          <div key={r.deviceId} className="flex items-center justify-between gap-2 px-2 py-0.5">
            <span className="text-slate-300 text-xs truncate" title={r.device?.hostname ?? `device ${r.deviceId}`}>
              {r.device?.hostname?.replace(/^RTR - /, '') ?? `device ${r.deviceId}`}
            </span>
            <span className="text-slate-400 font-mono text-xs">{r.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
